/**
 * Reads a response body without ever throwing.
 *
 * Shared by the DHA client and the VFS Client transport, which face the same three
 * realities: an endpoint that answers 204 with nothing at all, a proxy that answers
 * a 2xx with an HTML error page, and a body that fails mid-stream. Each returns
 * `undefined` rather than raising, so a caller reading an error body cannot itself
 * fail while doing so.
 *
 * Extracted rather than duplicated so the two clients cannot drift on what "no
 * usable body" means — the point at which a 204 starts throwing "Unexpected end of
 * JSON input" in one of them and not the other.
 */
export async function readBodySafely(response: Response): Promise<unknown> {
  try {
    const text = await response.text()
    if (text.trim().length === 0) return undefined
    try {
      return JSON.parse(text) as unknown
    } catch {
      // Not JSON — an HTML error page, or plain prose. Handed back as the string it
      // is, because `extractDetail` can still make something readable of it.
      return text
    }
  } catch {
    // The stream itself failed. There is nothing to report but its absence.
    return undefined
  }
}
